import React, { useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { useGetUserByIdQuery } from '../../../../api/endpoints/userAccount';
import { useLanguage } from '../../../../contexts/LanguageContext';

const UserAccountsTable = ({ users, loading, filters, onEdit, onNew, totalCount, lazyParams, onPage, onSort }) => {
    const { th, getCurrentLanguageId } = useLanguage();
    const [selectedUserId, setSelectedUserId] = React.useState(null);
    const [editingUserId, setEditingUserId] = React.useState(null);

    const {
        data: userData,
        refetch,
        isFetching,
        error
    } = useGetUserByIdQuery(
        { id: selectedUserId, languageId: getCurrentLanguageId() },
        {
            skip: !selectedUserId
        }
    );

    useEffect(() => {
        if (selectedUserId && userData?.isSuccessful && userData?.data && !isFetching) {
            onEdit(userData.data);
            setSelectedUserId(null);
            setEditingUserId(null);
        }
    }, [userData, onEdit, isFetching, selectedUserId]);

    useEffect(() => {
        if (error) {
            console.error('Error fetching user data:', error);
            setSelectedUserId(null);
            setEditingUserId(null);
        }
    }, [error]);

    const handleEdit = (rowData) => {
        if (editingUserId === rowData.id && isFetching) {
            return;
        }

        setSelectedUserId(null);
        setEditingUserId(null);

        setEditingUserId(rowData.id);
        setSelectedUserId(rowData.id);
    };

    const handleSort = (e) => {
        if (onSort) {
            onSort(e);
        }
    };

    const actionBodyTemplate = (rowData) => {
        const isCurrentlyEditing = editingUserId === rowData.id && isFetching;

        return (
            <div className="flex gap-2 justify-content-center">
                <i
                    onClick={() => handleEdit(rowData)}
                    className={`pi ${isCurrentlyEditing ? 'pi-spin pi-spinner' : 'pi-pencil'} cursor-pointer text-blue-500 hover:text-blue-700`}
                    style={{
                        fontSize: '1.1rem',
                        opacity: isCurrentlyEditing ? 0.7 : 1
                    }}
                    title={isCurrentlyEditing ? th('common.loading') : th('common.edit')}
                />
                <i className="pi pi-history cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('warrantyClaims.tooltips.history')}></i>
            </div>
        );
    };

    const statusBodyTemplate = (rowData) => {
        return <span className={`p-badge p-badge-${rowData.isActive === 1 ? 'success' : 'danger'}`}>{rowData.isActive === 1 ? th('userAccounts.active') : th('userAccounts.inactive')}</span>;
    };

    const emailNotificationBodyTemplate = (rowData) => {
        const raw = rowData.isEmailEnabled ?? rowData.is_email_enabled;
        const isEnabled = raw === true || raw === 1;
        return <span className={`p-badge p-badge-${isEnabled ? 'success' : 'danger'}`}>{isEnabled ? th('userAccounts.emailEnabled') : th('userAccounts.emailDisabled')}</span>;
    };

    const header = (
        <div className="flex justify-content-end align-items-center">
            <Button label={th('common.new')} icon="pi pi-plus" onClick={onNew} />
        </div>
    );

    const startRecord = lazyParams.first + 1;
    const endRecord = Math.min(lazyParams.first + users.length, totalCount);

    return (
        <DataTable
            value={users || []}
            paginator
            className="p-datatable-gridlines"
            showGridlines
            rows={lazyParams.rows}
            first={lazyParams.first}
            totalRecords={totalCount}
            lazy={true}
            dataKey="id"
            loading={loading}
            filters={filters}
            header={header}
            emptyMessage={th('userAccounts.emptyMessage')}
            rowsPerPageOptions={[5, 10, 25, 50]}
            tableStyle={{ minWidth: '50rem' }}
            onPage={onPage}
            onSort={handleSort}
            removableSort
            sortMode="single"
            sortField={lazyParams.sortField}
            sortOrder={lazyParams.sortOrder}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
            currentPageReportTemplate={`${startRecord} - ${endRecord} / ${totalCount}`}
        >
            <Column body={actionBodyTemplate} style={{ width: '5%' }} align="center" />
            <Column field="firstName" header={th('userAccounts.firstName')} sortable style={{ width: '15%' }} />
            <Column field="lastName" header={th('userAccounts.lastName')} sortable style={{ width: '15%' }} />
            <Column field="email" header={th('userAccounts.email')} sortable style={{ width: '20%' }} />
            <Column field="isEmailEnabled" header={th('userAccounts.emailNotification')} body={emailNotificationBodyTemplate} sortable style={{ width: '10%' }} />
            <Column field="isActive" header={th('userAccounts.status')} body={statusBodyTemplate} sortable style={{ width: '10%' }} />
        </DataTable>
    );
};

export default UserAccountsTable;
